import React, { FC, useState, ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Checkbox, FormControlLabel, FormGroup, Grid, TextField } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import ApprovedByAutocomplete from 'components/orders/order-details/approved-by-autocomplete/approved-by-autocomplete.component';
import { getEscapedMessage } from 'utils/escaped-message';
import { User } from 'models/user';
import { DeviationAction } from 'models/deviations';

type AddUpdateDeviationActionDialogProps = {
    open: boolean;
    onDialogClose: () => void;
    deviationId: string;
    deviationAction?: DeviationAction;
    onDeviationActionAdd: (deviationAction: DeviationAction, deviationId: string) => void;
    onDeviationActionUpdate: (deviationAction: DeviationAction, deviationId: string) => void;
};

const AddUpdateDeviationActionDialog: FC<AddUpdateDeviationActionDialogProps> = ({
    open,
    onDialogClose,
    deviationId,
    deviationAction,
    onDeviationActionAdd,
    onDeviationActionUpdate,
}: AddUpdateDeviationActionDialogProps) => {
    const { t: translate } = useTranslation(['common', 'deviations']);
    const [labTechnician, setLabTechnician] = useState<User>({} as User);
    const [visibleForCustomer, setVisibleForCustomer] = useState(false);
    const [report, setReport] = useState('');

    useEffect(() => {
        if (deviationAction?.addedOn) {
            setLabTechnician(deviationAction.technician as any);
            // DD-3494 rename: new field first, deprecated field as fallback until backfill.
            setVisibleForCustomer(Boolean(deviationAction.visibleForCustomer ?? deviationAction.visibleForDentist));
            setReport(deviationAction.report?.replace(new RegExp('<br/>', 'gi'), '\n'));
        }
    }, [deviationAction]);

    const handleDialogClose = () => {
        onDialogClose();
        setLabTechnician({} as User);
        setVisibleForCustomer(false);
        setReport('');
    };

    const selectTechnician = (technician: User | any) => {
        setLabTechnician(technician);
    };

    const handleVisibleForCustomerCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
        setVisibleForCustomer(event.target.checked);
    };

    const handleReportChange = (event: ChangeEvent<HTMLInputElement>) => {
        setReport(event.target.value);
    };

    const handleAddDeviationAction = () => {
        const formattedReport = getEscapedMessage(report);
        const data = {
            technician: labTechnician.fullName,
            report: formattedReport,
            addedOn: new Date(),
            // PR 1 of DD-3494 still sends the deprecated input field; the backend
            // dual-writes both. Switches to visibleForCustomer in PR 2.
            visibleForDentist: visibleForCustomer,
        };
        onDeviationActionAdd(data, deviationId);
        handleDialogClose();
    };

    const handleUpdateDeviationAction = () => {
        const formattedReport = getEscapedMessage(report);
        const data = {
            _id: deviationAction?._id,
            technician: labTechnician.fullName || (deviationAction?.technician as string),
            report: formattedReport,
            addedOn: new Date(),
            // PR 1 of DD-3494: deprecated input field, dual-written server-side.
            visibleForDentist: visibleForCustomer,
        };
        onDeviationActionUpdate(data, deviationId);
        handleDialogClose();
    };

    const isEmpty = <T extends object>(obj: T): boolean => {
        return Object.keys(obj).length === 0;
    };

    return (
        <DHDialog
            maxWidth="lg"
            fullWidth
            open={open}
            onClose={handleDialogClose}
            title={
                deviationAction?._id
                    ? translate('deviations:UPDATE_DEVIATION_ACTION')
                    : translate('deviations:ADD_DEVIATION_ACTION')
            }
            actions={
                <Button
                    key="submit"
                    disabled={isEmpty(labTechnician) || !report}
                    onClick={deviationAction?._id ? handleUpdateDeviationAction : handleAddDeviationAction}
                    variant="contained"
                    type="submit"
                >
                    {deviationAction?._id ? translate('common:UPDATE') : translate('common:ADD')}
                </Button>
            }
        >
            <Box>
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <ApprovedByAutocomplete
                            onTechnicianSelected={selectTechnician}
                            labTechnician={labTechnician}
                            dataTest="add-update-deviation-action-dialog.lab-technician-autocomplete"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            margin="dense"
                            label={translate('deviations:DEVIATION_ACTION_REPORT')}
                            onChange={handleReportChange}
                            value={report}
                            multiline
                            rows={12}
                            data-test="add-update-deviation-action-dialog.report-textfield"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={visibleForCustomer}
                                        onChange={handleVisibleForCustomerCheckbox}
                                    />
                                }
                                label={translate('deviations:VISIBLE_FOR_CUSTOMER_CHECKBOX')}
                                data-test="add-update-deviation-action-dialog.visible-for-dentist-checkbox"
                            />
                        </FormGroup>
                    </Grid>
                </Grid>
            </Box>
        </DHDialog>
    );
};

export default AddUpdateDeviationActionDialog;
